import {Fragment, useSyncExternalStore} from 'react'

import styles from './Universe26Banner.module.css'

const pad = (value: number) => String(value).padStart(2, '0')

const SEPARATOR = ' : '

export type CountdownSegment = {
  unit: 'days' | 'hours' | 'minutes' | 'seconds'
  value: string
  /** True for a leading segment that has run out (e.g. days once less than a day remains). */
  elapsed: boolean
}

export function getCountdownSegments(remainingMs: number): CountdownSegment[] {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000))
  const values = {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }

  let elapsed = true
  return (['days', 'hours', 'minutes', 'seconds'] as const).map(unit => {
    elapsed = elapsed && values[unit] === 0
    return {unit, value: pad(values[unit]), elapsed}
  })
}

export function formatCountdown(remainingMs: number): string {
  return getCountdownSegments(remainingMs)
    .map(segment => segment.value)
    .join(SEPARATOR)
}

function subscribe(onTick: () => void) {
  const id = setInterval(onTick, 1000)
  return () => clearInterval(id)
}

// Whole seconds keep consecutive snapshots equal within a tick.
const getNowInSeconds = () => Math.floor(Date.now() / 1000)

/**
 * The server snapshot is empty so SSR and hydration markup match; the client fills in the remaining
 * time right after hydration. The slot reserves its width until then.
 */
export function Countdown({to}: {to: string}) {
  const nowInSeconds = useSyncExternalStore(subscribe, getNowInSeconds, () => null)

  return (
    <span className={styles.countdown}>
      {nowInSeconds === null
        ? null
        : getCountdownSegments(Date.parse(to) - nowInSeconds * 1000).map(({unit, value, elapsed}, index) => (
            <Fragment key={unit}>
              {index > 0 ? SEPARATOR : null}
              <span className={elapsed ? styles.countdownElapsed : undefined}>{value}</span>
            </Fragment>
          ))}
    </span>
  )
}
